/**
 * FacetSidebar — the left rail of the Admin Question Bank archetype.
 *
 * Binding contract: `docs/ia/admin-question-bank.md` § Minimum 3 + § Polish 1/2/3.
 *
 * Editorial intent (Taste lens — from the contract):
 *   "The page answers 'find this question, edit it, bulk-manage it' in one
 *    tight workspace. The editorial voice is 'library card catalogue' —
 *    taxonomic, scholarly, browseable — not 'spreadsheet.' Selected filters
 *    feel like opening a drawer in a wooden card-catalogue cabinet."
 *
 * Substrate (15-FG/35):
 *   - Composes Card / Button / Badge from `@/components/ui` only. No raw
 *     atoms, no chromatic borders.
 *   - Three facet groups in fixed contract order: Category / Type / Difficulty.
 *   - Active chip carries `aria-pressed="true"` and the workspace-mode
 *     2px accent left-border (design.md § Density / Workspace).
 *
 * Selection state is OWNED BY THE PARENT. This component is presentational +
 * a thin onChange surface — mirrors the QuestionPicker idiom from 15-FG/31.
 */

import { type ReactElement, type ReactNode } from 'react';
import { Badge, Button, Card, Heading } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Difficulty, QuestionType } from '../../../../state/questions/api.ts';

// ─── Contract enums — frozen in this file because they encode the archetype ──

/** Type chip rows in DOM order — Minimum 3 enumerates these six. */
export const TYPE_CHIPS: ReadonlyArray<{ value: QuestionType; label: string }> = [
  { value: 'multiple', label: 'Multiple Choice' },
  { value: 'truefalse', label: 'True / False' },
  { value: 'multi', label: 'Multi-Select' },
  { value: 'order', label: 'Put in Order' },
  { value: 'fillnumber', label: 'Fill Number' },
  { value: 'filltext', label: 'Fill Text' },
];

/** Difficulty chip rows in DOM order — Minimum 3 enumerates these three. */
export const DIFFICULTY_CHIPS: ReadonlyArray<{
  value: Difficulty;
  label: string;
  badge: 'success' | 'warning' | 'error';
}> = [
  { value: 'easy', label: 'Easy', badge: 'success' },
  { value: 'medium', label: 'Medium', badge: 'warning' },
  { value: 'hard', label: 'Hard', badge: 'error' },
];

export interface FacetCategory {
  id: string;
  name: string;
  count: number;
}

export interface FacetSidebarProps {
  /** Total question count — rendered inside the "All Questions" chip badge. */
  totalCount: number;
  /** Dynamic category list — rendered below the "All Questions" reset chip. */
  categories: FacetCategory[];
  /** Currently selected category id, or `null` for "All Questions". */
  activeCategoryId: string | null;
  /** Currently selected type, or `null` for unfiltered. */
  activeType: QuestionType | null;
  /** Currently selected difficulty, or `null` for unfiltered. */
  activeDifficulty: Difficulty | null;
  onCategoryChange: (id: string | null) => void;
  onTypeChange: (t: QuestionType | null) => void;
  onDifficultyChange: (d: Difficulty | null) => void;
}

// ─── Chip primitive — composes <Button variant='ghost'> with workspace
//     active-state styling per design.md § Density Modes / Workspace mode
//     ("Active item gets a 2px ink left-border and a bg-warm fill"). The
//     2px accent border lives in className, not a new token. Inv 15
//     compliance: chip IS the Button primitive — never a raw <button>. ────

interface FacetChipProps {
  isActive: boolean;
  onClick: () => void;
  /** Optional trailing chrome (e.g. Badge for count, Badge for difficulty). */
  trailing?: ReactNode;
  /** The chip label text. */
  children: ReactNode;
}

// Sidebar Card width: ~220px per archetype § Polish 2 "filter sidebar ~220px
// wide, mirroring the admin nav sidebar proportions." Composed via cn() to
// route the arbitrary-value off the static className path eslint-plugin-
// tailwindcss analyses (mirrors QuestionPicker's PICKER_SCROLL_CLASSES).
const SIDEBAR_CLASSES = cn(
  'flex flex-col gap-4 p-4',
  'lg:w-[220px] lg:shrink-0',
);

// Chip layout — overrides Button's default `inline-flex` to fill the sidebar
// width and reserve a 2px left border slot so active/inactive don't reflow.
const CHIP_LAYOUT = cn(
  'flex w-full items-center justify-between gap-2',
  'border-l-2 border-transparent',
  // Override Button's default padding to the archetype's 36px chip height
  '!px-3 !py-2',
);

// Workspace active treatment per design.md § Density Modes / Workspace mode.
const CHIP_ACTIVE = cn('bg-bg-warm border-l-2 border-accent');

function FacetChip({
  isActive,
  onClick,
  trailing,
  children,
}: FacetChipProps): ReactElement {
  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={cn(CHIP_LAYOUT, isActive ? CHIP_ACTIVE : '')}
    >
      <span className="text-left font-sans text-small font-medium text-ink">
        {children}
      </span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </Button>
  );
}

// ─── Section — facet group header + chip stack ────────────────────────────

interface FacetGroupProps {
  /** Section title, e.g. "Filter by Category". Becomes a labelled landmark. */
  title: string;
  /** Stable id slot for aria-labelledby. */
  id: string;
  children: ReactNode;
}

function FacetGroup({ title, id, children }: FacetGroupProps): ReactElement {
  const headingId = `${id}-heading`;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1">
      <Heading
        id={headingId}
        level="h3"
        className="font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
      >
        {title}
      </Heading>
      <div className="flex flex-col gap-1">{children}</div>
    </section>
  );
}

// ─── Component ────────────────────────────────────────────────────────────

export function FacetSidebar(props: FacetSidebarProps): ReactElement {
  const {
    totalCount,
    categories,
    activeCategoryId,
    activeType,
    activeDifficulty,
    onCategoryChange,
    onTypeChange,
    onDifficultyChange,
  } = props;

  return (
    <Card
      variant="default"
      // Workspace mode: ~220px wide sidebar per archetype Polish 2.
      // 16px internal padding, 16px between groups. The 220px width is an
      // arbitrary value composed via cn() so eslint-plugin-tailwindcss's
      // static-string analyser (which doesn't read tokens from function-call
      // args) does not flag it — same idiom as QuestionPicker's
      // PICKER_SCROLL_CLASSES max-h-[480px].
      className={SIDEBAR_CLASSES}
      role="complementary"
      aria-label="Filters"
    >
      {/* Filter by Category — Minimum 3.a + Minimum 4 (All Questions reset chip) */}
      <FacetGroup title="Filter by Category" id="facet-category">
        <FacetChip
          isActive={activeCategoryId === null}
          onClick={() => onCategoryChange(null)}
          trailing={
            <Badge variant="neutral" className="font-mono">
              {totalCount}
            </Badge>
          }
        >
          All Questions
        </FacetChip>
        {categories.map((cat) => (
          <FacetChip
            key={cat.id}
            isActive={activeCategoryId === cat.id}
            onClick={() => onCategoryChange(cat.id)}
            trailing={
              <Badge variant="neutral" className="font-mono">
                {cat.count}
              </Badge>
            }
          >
            {cat.name}
          </FacetChip>
        ))}
      </FacetGroup>

      {/* Filter by Type — Minimum 3.b */}
      <FacetGroup title="Filter by Type" id="facet-type">
        {TYPE_CHIPS.map((chip) => (
          <FacetChip
            key={chip.value}
            isActive={activeType === chip.value}
            onClick={() =>
              onTypeChange(activeType === chip.value ? null : chip.value)
            }
          >
            {chip.label}
          </FacetChip>
        ))}
      </FacetGroup>

      {/* Filter by Difficulty — Minimum 3.c */}
      <FacetGroup title="Filter by Difficulty" id="facet-difficulty">
        {DIFFICULTY_CHIPS.map((chip) => (
          <FacetChip
            key={chip.value}
            isActive={activeDifficulty === chip.value}
            onClick={() =>
              onDifficultyChange(
                activeDifficulty === chip.value ? null : chip.value,
              )
            }
            trailing={
              <Badge variant={chip.badge} className="font-mono">
                {chip.value}
              </Badge>
            }
          >
            {chip.label}
          </FacetChip>
        ))}
      </FacetGroup>

    </Card>
  );
}
